import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, BookOpen, Activity } from 'lucide-react';

const SpinalStenosisCondition = () => {
  const [openFaqId, setOpenFaqId] = useState(null);

  const toggleFaq = (id) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  const pageData = {
      title: "Spinal Stenosis Treatment in New York City",
      metaDescription: "Expert care for Spinal Stenosis in New York. Discover personalized treatments to relieve pain and improve mobility.",
      metaKeywords: "Spinal Stenosis, spine treatment NYC, Dr. Tindel, back pain relief",
      heroDescription: "Comprehensive solutions for Spinal Stenosis with personalized treatment plans to restore mobility and reduce discomfort.",
      forWho: [
        "Spinal stenosis refers to a narrowing of the spinal canal, which can put pressure on the spinal cord or nerve roots. It most commonly occurs in the cervical (neck) or lumbar (lower back) areas and often results from age-related changes like disc bulges, bone spurs, or thickened ligaments. Symptoms may include pain, numbness, or cramping in the arms or legs—especially when walking or standing.",
        "Many individuals with mild stenosis remain symptom-free, but as the canal narrows further, nerve compression can make daily tasks like walking or lifting objects increasingly difficult. In severe cases, bladder or bowel function might be affected."
      ],
      conditionInfo: [
          "If you have unexplained pain or weakness that eases when bending forward (like leaning on a shopping cart), spinal stenosis could be involved. Our spine experts at the NY Center for Spinal Disorders can perform imaging tests to confirm stenosis and suggest ways to alleviate pressure on the nerves.",
      ],
      treatment: [
          "Treatment may include physical therapy, pain management injections, or minimally invasive procedures.",
          "In severe cases, surgical options such as decompression or spinal fusion may be considered.",
          "Our approach focuses on relieving symptoms while preserving spinal mobility."
      ],
      postCare: [
          "We provide ongoing follow-up care to monitor progress and adjust treatment as needed.",
          "Our rehabilitation programs focus on strengthening the back and improving flexibility."
      ],
      nonSurgicalOptions: [
          "Physical therapy & core strengthening",
          "Epidural steroid injections",
          "Activity modification & lifestyle adjustments"
      ],
      faqs: [
          {
              id: 1,
              question: "Can Spinal Stenosis get worse over time?",
              answer: "Yes, if left untreated, symptoms can gradually worsen. Early intervention helps manage progression."
          },
          {
              id: 2,
              question: "Do all cases require surgery?",
              answer: "No, many patients find relief with conservative treatments such as therapy and injections."
          }
      ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Helmet>
      <title>{pageData.title} - NYC for Spinal Disorders</title>
      <meta name="description" content={pageData.metaDescription} />
      <meta name="keywords" content={pageData.metaKeywords} />
      <meta name="author" content="New York Center for Spinal Disorders" />
      <meta property="og:title" content={pageData.title} />
      <meta property="og:description" content={pageData.metaDescription} />
      <meta property="og:image" content="/images/logo-header.png" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <Header />

      
      <div className="flex items-center relative min-h-[40vh] bg-gradient-to-r from-[#003666] to-[#0062B1] py-8">
        <div className="absolute inset-0">
          <img
            src="../images/circles.jpg"
            alt={pageData.title}
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative h-full max-w-6xl mx-auto px-4 flex items-center">
          <div>
            <h1 className="text-5xl font-bold text-white mb-4">{pageData.title}</h1>
            <p className="text-2xl text-blue-100">{pageData.heroDescription}</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg">
        <div className="max-w-6xl mx-auto py-6 px-4">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition">
              Schedule Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center px-6 py-3 border-2 border-blue-900 text-blue-900 rounded-full hover:bg-blue-50 transition">
              <Phone className="mr-2 w-4 h-4" /> Call Now
            </a>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Condition Overview</h2>
                {pageData.forWho.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <Activity className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">What Patients Need to Know</h2>
                <ul className="space-y-4 text-gray-700">
                  {pageData.conditionInfo.map((info, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>





        <div className="bg-gradient-to-r from-[#003666] to-[#0062B1] rounded-xl shadow-lg p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Patient-Centered Approach</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our dedicated team of board-certified orthopedic spine physicians at New York Center for Spinal Disorders works together to guide you every step of the way, always prioritizing your comfort and well-being.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-full font-bold hover:bg-blue-100 transition duration-300">
              Schedule Consultation
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white/10 transition duration-300">
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SpinalStenosisCondition;